// Robustness layer (Stage 2): make single-camera 2D estimates repeatable by
// (a) gating low-confidence landmark samples out of the per-frame signals, and
// (b) being honest about the near/far-leg asymmetry that is inherent to a
// lateral recording — the far leg is partially occluded by the body, so its
// landmarks carry systematically lower visibility and its derived metrics
// deserve a lower confidence than the camera-side leg.
//
// Everything here is a pure function over PoseFrame[] / number[], so it is fully
// unit-testable and has no browser/MediaPipe dependency. All helpers are no-ops
// on fully-visible input (visibility ≥ gate everywhere), which keeps the
// existing high-quality fixtures — and therefore stored high-quality sessions —
// numerically unchanged.

import { Joints, type PoseFrame, type Side } from "../pose/landmarks";
import { deriveConfidence, minConfidence, type Confidence } from "./bands";
import { mean } from "./signal";

/** Default per-landmark visibility below which a sample is treated as unreliable. */
export const VISIBILITY_GATE = 0.5;

/**
 * Replace low-visibility samples in a 1-D series by interpolation from the
 * nearest trustworthy neighbours, so a momentarily occluded/jittered landmark
 * can't poison a downstream extremum (we read 5th/95th percentiles for ROM
 * excursions, which are exactly what a single garbage spike distorts).
 *
 *   • interior gap  → linear interpolation between the bracketing good samples
 *   • leading gap   → hold the first good sample
 *   • trailing gap  → hold the last good sample
 *
 * If every sample is good, the input values are returned unchanged (identity),
 * so this is a no-op on high-quality clips. If NO sample is good, the series is
 * returned as-is (nothing trustworthy to anchor on).
 */
export function gateSeries(
  values: number[],
  visibility: number[],
  minVis = VISIBILITY_GATE,
): number[] {
  const n = values.length;
  const out = values.slice();
  if (n === 0) return out;

  const good = (i: number): boolean => (visibility[i] ?? 1) >= minVis;

  let anyBad = false;
  let firstGood = -1;
  for (let i = 0; i < n; i++) {
    if (good(i)) {
      if (firstGood === -1) firstGood = i;
    } else {
      anyBad = true;
    }
  }
  if (!anyBad || firstGood === -1) return out; // all good, or nothing to anchor on

  let i = 0;
  while (i < n) {
    if (good(i)) {
      i++;
      continue;
    }
    let j = i - 1;
    while (j >= 0 && !good(j)) j--;
    let k = i + 1;
    while (k < n && !good(k)) k++;

    if (j < 0) {
      // leading gap: hold the first good value
      for (let m = i; m < k; m++) out[m] = values[k];
      i = k;
    } else if (k >= n) {
      // trailing gap: hold the last good value
      for (let m = i; m < n; m++) out[m] = values[j];
      i = n;
    } else {
      // interior gap: linear interpolation across [j, k]
      const span = k - j;
      for (let m = i; m < k; m++) {
        const t = (m - j) / span;
        out[m] = values[j] * (1 - t) + values[k] * t;
      }
      i = k;
    }
  }
  return out;
}

const LEG_JOINTS = (j: Joints, s: Side) => [j.hip(s), j.knee(s), j.ankle(s), j.footIndex(s)];

/** Per-frame trustworthiness of a leg = min visibility across its joints. */
export function legVisibilitySeries(frames: PoseFrame[], side: Side): number[] {
  return frames.map((f) => {
    const j = new Joints(f);
    return Math.min(...LEG_JOINTS(j, side).map((l) => l.visibility));
  });
}

/** Mean trustworthiness of a leg over the clip (0..1). */
export function legVisibility(frames: PoseFrame[], side: Side): number {
  return frames.length ? mean(legVisibilitySeries(frames, side)) : 0;
}

export interface LegProximity {
  /** Camera-side leg (higher visibility) — the more reliable side. */
  near: Side;
  /** Occluded far leg (lower visibility) — the less reliable side. */
  far: Side;
  nearVisibility: number;
  farVisibility: number;
  /** nearVisibility − farVisibility; large ⇒ a clear near/far split (true lateral). */
  separation: number;
}

/**
 * Classify which leg is camera-near vs occluded-far from the visibility split.
 * In a frontal recording both legs are similarly visible, so `separation` is
 * small and the near/far labels are essentially arbitrary — callers should use
 * `separation` to decide whether the distinction is meaningful.
 */
export function classifyLegProximity(frames: PoseFrame[]): LegProximity {
  const visL = legVisibility(frames, "L");
  const visR = legVisibility(frames, "R");
  const near: Side = visL >= visR ? "L" : "R";
  const far: Side = near === "L" ? "R" : "L";
  const nearVisibility = Math.max(visL, visR);
  const farVisibility = Math.min(visL, visR);
  return {
    near,
    far,
    nearVisibility,
    farVisibility,
    separation: nearVisibility - farVisibility,
  };
}

/**
 * Per-side capture confidence: the global confidence, additionally capped by
 * what that side's own visibility and cycle count support. It can only LOWER a
 * side's confidence relative to the whole-clip value, never raise it — so the
 * occluded far leg is reported more cautiously while the global gating-based
 * floor still holds (a bad clip stays "low" on every side).
 */
export function perSideConfidence(
  frames: PoseFrame[],
  cyclesBySide: Record<Side, number>,
  globalConfidence: Confidence,
): Record<Side, Confidence> {
  const forSide = (s: Side): Confidence =>
    minConfidence(globalConfidence, deriveConfidence(legVisibility(frames, s), cyclesBySide[s]));
  return { L: forSide("L"), R: forSide("R") };
}
